import Stack from '@mui/material/Stack';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Autocomplete,
} from '@mui/material';
import { CheckCircleOutlined } from '@mui/icons-material';
import { PPGFCountries } from '@joindeed/paypal-public';

import {
  Modal,
  ModalContents,
  ModalTitle,
  ModalButton,
  ModalTypography,
} from 'components/Modal/Modal';
import { showErrorAction } from 'containers/Notification/actions';
import {
  useOutreachNonprofitNominationMutation,
  OutreachEmailId,
  NonprofitNomination,
} from 'generated/graphql';

import { OutreachLanguageCountry } from './constants';

const emailLabelById = {
  nonprofitNominationOutreachInitial: 'Initial',
  // @NOTE-EV: Reminder emails are being discontinued but we still want to keep track of those that have been sent until now
  nonprofitNominationOutreachReminder: 'Reminder',
  nonprofitNominationOutreachFinal: 'Final',
};

const OutreachModal = ({
  onClose,
  nomination,
}: {
  onClose: () => void;
  nomination: NonprofitNomination;
}): React.ReactElement => {
  const dispatch = useDispatch();
  const [updateNonprofitNominationMutation, { loading }] = useOutreachNonprofitNominationMutation();
  const [emailId, setEmailId] = useState<null | OutreachEmailId>(null);
  const [confirm, setConfirm] = useState(false);
  const [templateLocaleCode, setTemplateLocaleCode] = useState('en-US');

  const handleSubmit = async () => {
    try {
      if (emailId) {
        await updateNonprofitNominationMutation({
          variables: {
            nominationId: nomination.id,
            emailId,
            templateLocaleCode,
          },
        });
      }
      onClose();
    } catch (e) {
      dispatch(showErrorAction('There was an error outreaching this nomination'));
    }
  };

  if (
    confirm &&
    // Just to make TS happy
    emailId
  ) {
    return (
      <Modal open disableAutoFocus>
        <ModalContents
          onClickClose={onClose}
          body={
            <>
              <ModalTitle>Outreach: Send {emailLabelById[emailId]} email</ModalTitle>
              <ModalTypography>
                By continuing, you will send an email to the {nomination.nonprofitName} nonprofit.
                Do you want to continue?
              </ModalTypography>
              <Autocomplete
                style={{ paddingTop: 12, paddingBottom: 20 }}
                value={OutreachLanguageCountry.find((item) => item.value === templateLocaleCode)}
                options={OutreachLanguageCountry}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                getOptionLabel={(option) => option.label || ''}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setTemplateLocaleCode(newValue.value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country and Language of Email Template"
                    required
                    fullWidth
                    variant="standard"
                    helperText="Select language and country - By default the selected template is for english-speaking US-based nonprofits"
                  />
                )}
              />
              <ModalTypography>Do you wish to continue with this update?</ModalTypography>
            </>
          }
          actions={
            <>
              <ModalButton disabled={loading} onClick={onClose} variant="outlined">
                No
              </ModalButton>
              &nbsp;
              <ModalButton
                disabled={loading || !emailId}
                onClick={() => {
                  handleSubmit();
                }}
              >
                Yes, send the email
              </ModalButton>
            </>
          }
        />
      </Modal>
    );
  }

  return (
    <Modal open disableAutoFocus>
      <ModalContents
        onClickClose={onClose}
        body={
          <>
            <ModalTitle>Nonprofit Nomination: Outreach</ModalTitle>
            <Stack>
              {!PPGFCountries.includes(
                nomination.country?.slice(0, 2).toUpperCase() as (typeof PPGFCountries)[number]
              ) && (
                <Alert severity="warning" sx={{ marginBottom: 2 }}>
                  Note that these outreach emails are PPGF specific, and should not be sent to NPOs
                  outside of the {PPGFCountries.slice(0, -1).join(', ')} and{' '}
                  {PPGFCountries[PPGFCountries.length - 1]}.
                </Alert>
              )}
              <FormControl>
                <RadioGroup onChange={(e) => setEmailId(e.target.value as OutreachEmailId)}>
                  {(
                    [
                      'nonprofitNominationOutreachInitial',
                      'nonprofitNominationOutreachFinal',
                    ] as const
                  ).map((id) => (
                    <FormControlLabel
                      value={id}
                      title={nomination.emails?.[id] ? 'Already sent' : undefined}
                      control={
                        nomination.emails?.[id] ? (
                          <CheckCircleOutlined style={{ margin: 9 }} color="success" />
                        ) : (
                          <Radio />
                        )
                      }
                      label={`${emailLabelById[id]} email`}
                      disabled={nomination.emails?.[id]}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Stack>
          </>
        }
        actions={
          <>
            <ModalButton disabled={loading} onClick={onClose} variant="outlined">
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton
              disabled={loading || !emailId}
              onClick={() => {
                setConfirm(true);
              }}
            >
              {loading ? <CircularProgress size={21} /> : 'Send'}
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};

export default OutreachModal;
